import { useEffect, useRef } from "react";

const OVERLAY_STATE_KEY = "__gestorOverlayId";
const overlayStack: string[] = [];
let overlaySequence = 0;
let ignoreProgrammaticPopState = false;
let ignoreProgrammaticPopStateTimer: number | undefined;

const currentUrlPath = () =>
  `${window.location.pathname}${window.location.search}${window.location.hash}`;

const removeFromStack = (id: string) => {
  const index = overlayStack.lastIndexOf(id);
  if (index !== -1) overlayStack.splice(index, 1);
};

const clearIgnoredProgrammaticPopState = () => {
  ignoreProgrammaticPopState = false;
  if (ignoreProgrammaticPopStateTimer !== undefined) {
    window.clearTimeout(ignoreProgrammaticPopStateTimer);
    ignoreProgrammaticPopStateTimer = undefined;
  }
};

const scheduleIgnoredProgrammaticPopStateClear = (delay: number) => {
  if (ignoreProgrammaticPopStateTimer !== undefined) {
    window.clearTimeout(ignoreProgrammaticPopStateTimer);
  }
  ignoreProgrammaticPopStateTimer = window.setTimeout(
    clearIgnoredProgrammaticPopState,
    delay
  );
};

const consumeOverlayHistoryEntry = () => {
  ignoreProgrammaticPopState = true;
  window.history.back();
  scheduleIgnoredProgrammaticPopStateClear(1000);
};

export function useCloseOnBack(
  open: boolean,
  onClose: (() => void) | undefined,
  enabled = true
) {
  const idRef = useRef("");
  const hasHistoryEntryRef = useRef(false);
  const openRef = useRef(open);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    openRef.current = open;
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!enabled || !open || hasHistoryEntryRef.current || !onCloseRef.current) return;

    const id = idRef.current || `overlay-${++overlaySequence}`;
    idRef.current = id;
    overlayStack.push(id);
    hasHistoryEntryRef.current = true;

    const currentState =
      window.history.state && typeof window.history.state === "object"
        ? window.history.state
        : {};
    window.history.pushState(
      { ...currentState, [OVERLAY_STATE_KEY]: id },
      "",
      currentUrlPath()
    );
  }, [enabled, open]);

  useEffect(() => {
    if (!enabled || open || !hasHistoryEntryRef.current) return;

    const id = idRef.current;
    const wasTop = overlayStack[overlayStack.length - 1] === id;
    removeFromStack(id);
    hasHistoryEntryRef.current = false;

    if (wasTop && window.history.state?.[OVERLAY_STATE_KEY] === id) {
      consumeOverlayHistoryEntry();
    }
  }, [enabled, open]);

  useEffect(() => {
    if (!enabled) return undefined;

    const handlePopState = () => {
      if (ignoreProgrammaticPopState) {
        scheduleIgnoredProgrammaticPopStateClear(0);
        return;
      }

      const id = idRef.current;
      if (!id || !hasHistoryEntryRef.current) return;
      if (overlayStack[overlayStack.length - 1] !== id) return;

      removeFromStack(id);
      hasHistoryEntryRef.current = false;
      if (openRef.current) onCloseRef.current?.();
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [enabled]);

  useEffect(
    () => () => {
      const id = idRef.current;
      if (!id) return;

      const hadHistoryEntry = hasHistoryEntryRef.current;
      const wasTop = overlayStack[overlayStack.length - 1] === id;
      removeFromStack(id);
      hasHistoryEntryRef.current = false;

      if (hadHistoryEntry && wasTop && window.history.state?.[OVERLAY_STATE_KEY] === id) {
        consumeOverlayHistoryEntry();
      }
    },
    []
  );
}
